import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { useLanguage } from "../App";
import Header from "../components/Header";
import Footer from "../components/Footer";
import BookingModal from "../components/BookingModal";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { CheckCircle, Users, User, ArrowLeft } from "lucide-react";
import { coursesData } from "../data/coursesData";

const lessonConfig = {
  "kitesurf-lessons": {
    key: "kitesurf",
    title: { en: "Kitesurf Lessons", es: "Lecciones de Kitesurf", de: "Kitesurf-Unterricht", fr: "Cours de Kitesurf" },
    subtitle: { en: "Professional IKO certified instruction for all levels", es: "Instruccion profesional certificada IKO para todos los niveles", de: "Professioneller IKO-zertifizierter Unterricht fur alle Levels", fr: "Instruction professionnelle certifiee IKO pour tous niveaux" },
    color: "from-blue-600 to-cyan-500",
    borderColor: "hover:border-blue-400",
    priceColor: "text-blue-600",
    bgColor: "bg-blue-50",
    btnColor: "from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600",
    badgeText: "IKO Certification",
    badgeClass: "bg-green-100 text-green-700",
    heroImage: "https://customer-assets.emergentagent.com/job_53c07cd7-1ab5-4cff-8752-32dcf9dc74c8/artifacts/ivcxbnxg_IMG_3878.jpeg",
    images: {
      "beginner-kitesurf": "https://customer-assets.emergentagent.com/job_53c07cd7-1ab5-4cff-8752-32dcf9dc74c8/artifacts/ivcxbnxg_IMG_3878.jpeg",
      "intermediate-kitesurf": "https://customer-assets.emergentagent.com/job_53c07cd7-1ab5-4cff-8752-32dcf9dc74c8/artifacts/fmucn057_IMG_3869.jpeg",
      "private-kitesurf": "https://customer-assets.emergentagent.com/job_53c07cd7-1ab5-4cff-8752-32dcf9dc74c8/artifacts/pjzamref_IMG_3884.jpeg",
    },
  },
  "surf-lessons": {
    key: "surf",
    title: { en: "Surf Courses", es: "Cursos de Surf", de: "Surf-Kurse", fr: "Cours de Surf" },
    subtitle: { en: "Catch your first wave in Dakhla", es: "Atrapa tu primera ola en Dakhla", de: "Fange deine erste Welle in Dakhla", fr: "Attrapez votre premiere vague a Dakhla" },
    color: "from-orange-500 to-yellow-500",
    borderColor: "hover:border-orange-400",
    priceColor: "text-orange-600",
    bgColor: "bg-orange-50",
    btnColor: "from-orange-500 to-yellow-500 hover:from-orange-600 hover:to-yellow-600",
    badgeText: "All Gear Included",
    badgeClass: "bg-blue-100 text-blue-700",
    heroImage: "https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/7txvwmsl_IMG_4362.jpeg",
    images: {
      "beginner-surf": "https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/6zvkg0u5_IMG_4363.jpeg",
      "private-surf": "https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/zsgf3sep_IMG_4370.jpeg",
      "rental-surf": "https://customer-assets.emergentagent.com/job_acc6b5c6-4c76-49ba-a1fd-498cb047a8ad/artifacts/6hn650kv_IMG_4369.jpeg",
    },
  },
  "wingfoil-lessons": {
    key: "wingfoil",
    title: { en: "Wing Foil Lessons", es: "Lecciones de Wing Foil", de: "Wing Foil Unterricht", fr: "Cours de Wing Foil" },
    subtitle: { en: "Professional Wingfoil courses in Dakhla", es: "Cursos profesionales de Wingfoil en Dakhla", de: "Professionelle Wingfoil-Kurse in Dakhla", fr: "Cours professionnels de Wingfoil a Dakhla" },
    color: "from-purple-600 to-pink-600",
    borderColor: "hover:border-purple-400",
    priceColor: "text-purple-600",
    bgColor: "bg-purple-50",
    btnColor: "from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700",
    badgeText: "All Gear Included",
    badgeClass: "bg-purple-100 text-purple-700",
    heroImage: "https://images.unsplash.com/photo-1666175497168-48b72df461cf?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwzfHx3aW5nZm9pbCUyMHdpbmclMjBmb2lsJTIwd2F0ZXIlMjBzcG9ydHxlbnwwfHx8fDE3NzM4ODY5MTJ8MA&ixlib=rb-4.1.0&q=85",
    images: {},
  },
};

const LessonDetailPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [bookingModalOpen, setBookingModalOpen] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedPricing, setSelectedPricing] = useState({});

  const config = lessonConfig[slug];
  if (!config) return <Navigate to="/lessons" replace />;

  const courses = coursesData[config.key];

  const handlePricingChange = (courseId, value) => {
    setSelectedPricing((prev) => ({ ...prev, [courseId]: value }));
  };

  const getPrice = (course) => {
    const idx = selectedPricing[course.id] || 0;
    return course.pricing[idx];
  };

  const handleBookNow = (course) => {
    const price = getPrice(course);
    setSelectedCourse({ courseId: course.id, priceInfo: price });
    setBookingModalOpen(true);
  };

  const isHoursBased = (course) => course.pricing[0]?.hours !== undefined;

  return (
    <div className="min-h-screen bg-white">
      <Header onBookingClick={() => setBookingModalOpen(true)} />

      {/* Hero */}
      <section
        className="pt-28 pb-20 relative"
        style={{ backgroundImage: `url(${config.heroImage})`, backgroundSize: "cover", backgroundPosition: "center" }}
      >
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/lessons" className="inline-flex items-center gap-2 text-white/80 hover:text-white text-sm mb-6 transition-colors" data-testid="back-to-lessons">
            <ArrowLeft className="h-4 w-4" />
            {language === "es" ? "Volver a lecciones" : language === "de" ? "Zuruck zu Kursen" : language === "fr" ? "Retour aux cours" : "Back to lessons"}
          </Link>
          <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3" data-testid="lesson-detail-title">
            {config.title[language]}
          </h1>
          <p className="text-lg text-white/80">{config.subtitle[language]}</p>
        </div>
      </section>

      {/* Course Cards */}
      <section className="py-16 -mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {courses.map((course) => {
              const currentPrice = getPrice(course);
              const courseImage = config.images[course.id];

              return (
                <Card key={course.id} className={`border-2 ${config.borderColor} transition-all hover:shadow-xl overflow-hidden bg-white`} data-testid={`course-card-${course.id}`}>
                  {courseImage && (
                    <div className="h-48 bg-cover bg-center relative" style={{ backgroundImage: `url(${courseImage})` }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                      {course.maxStudents && (
                        <div className={`absolute top-3 right-3 ${
                          course.type === "private" ? "bg-purple-600" : course.type === "semi-private" ? "bg-cyan-600" : "bg-blue-600"
                        } text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1`}>
                          {course.type === "group" ? <Users className="h-3 w-3" /> : <User className="h-3 w-3" />}
                          Max {course.maxStudents}
                        </div>
                      )}
                    </div>
                  )}
                  {!courseImage && course.maxStudents && (
                    <div className="px-6 pt-6">
                      <div className={`inline-block ${
                        course.type === "private" ? "bg-purple-600" : course.type === "rental" ? "bg-pink-600" : "bg-cyan-600"
                      } text-white px-3 py-1 rounded-full text-xs font-semibold`}>
                        {course.type === "private" ? <><User className="h-3 w-3 inline mr-1" />1-on-1</> :
                         course.type === "rental" ? "Rental" :
                         <><Users className="h-3 w-3 inline mr-1" />Max {course.maxStudents}</>}
                      </div>
                    </div>
                  )}
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{course.name[language]}</h3>
                    <p className="text-gray-600 text-sm mb-5">{course.description[language]}</p>

                    {/* Pricing selector */}
                    <div className="mb-4">
                      <Select
                        value={String(selectedPricing[course.id] || 0)}
                        onValueChange={(value) => handlePricingChange(course.id, parseInt(value))}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {course.pricing.map((option, idx) => (
                            <SelectItem key={idx} value={String(idx)}>
                              {isHoursBased(course) ? `${option.hours} hours` : option.duration} - €{option.price}{option.unit ? `/${option.unit}` : ""}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Price display */}
                    <div className={`${config.bgColor} p-3 rounded-lg mb-4`}>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-700 text-sm">
                          {isHoursBased(course) ? `${currentPrice.hours} hours` : currentPrice.duration}
                        </span>
                        <span className={`text-2xl font-bold ${config.priceColor}`}>
                          €{currentPrice.price}
                          {currentPrice.unit && <span className="text-sm">/{currentPrice.unit}</span>}
                        </span>
                      </div>
                      {course.extraHour && (
                        <p className="text-xs text-gray-500 mt-1">Extra hour: €{course.extraHour}</p>
                      )}
                    </div>

                    {/* Highlights */}
                    <ul className="space-y-1.5 mb-4">
                      {course.highlights[language].map((h, idx) => (
                        <li key={idx} className="flex items-start gap-2">
                          <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-700 text-xs">{h}</span>
                        </li>
                      ))}
                    </ul>

                    <Badge className={`mb-4 ${config.badgeClass}`}>{config.badgeText}</Badge>

                    <Button
                      onClick={() => handleBookNow(course)}
                      className={`w-full bg-gradient-to-r ${config.btnColor} text-white`}
                      data-testid={`book-now-${course.id}`}
                    >
                      {language === "es" ? "Reservar ahora" : language === "de" ? "Jetzt buchen" : language === "fr" ? "Reserver maintenant" : "Book Now"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <Footer />
      <BookingModal isOpen={bookingModalOpen} onClose={() => { setBookingModalOpen(false); setSelectedCourse(null); }} selectedCourse={selectedCourse} />
    </div>
  );
};

export default LessonDetailPage;
